/**
 * Field Mappings Module
 *
 * Provides field mapping functions for different PDF templates.
 * Each template has its own mapping that converts CharacterSheetData
 * to the specific field names used in that PDF.
 */

import type { CharacterSheetData, IgnusCharacterSheetData, PdfFieldMapping } from '../types';
import { mapWotcFields } from './wotcMapping';
import { mapIgnusArcanaFields } from './ignusArcanaMapping';
import { mapSrdHtmlFields } from './srdMapping';

/**
 * Field mapper function type
 */
export type FieldMapper = (data: CharacterSheetData | IgnusCharacterSheetData) => PdfFieldMapping[];

/**
 * Registry of field mappers by mapping ID
 */
const fieldMapperRegistry: Record<string, FieldMapper> = {
  // WotC official template
  'wotc': mapWotcFields,

  // Ignus templates
  'ignus-arcana': mapIgnusArcanaFields,
  'ignus-pragmata': mapWotcFields, // TODO: Create dedicated mapping

  // SRD HTML templates (Classic and Compact)
  'srd-html': mapSrdHtmlFields,

  // Legacy SRD mappings (kept for backward compatibility)
  'srd-arcana': mapWotcFields, // TODO: Create dedicated mapping
  'srd-pragmata': mapWotcFields, // TODO: Create dedicated mapping
};

/**
 * Get the field mapper for a specific mapping ID
 * Falls back to WotC mapping if not found
 */
export function getFieldMapper(mappingId: string): FieldMapper {
  return fieldMapperRegistry[mappingId] ?? mapWotcFields;
}

/**
 * Register a custom field mapper
 */
export function registerFieldMapper(mappingId: string, mapper: FieldMapper): void {
  fieldMapperRegistry[mappingId] = mapper;
}

/**
 * Get all registered mapping IDs
 */
export function getAvailableMappingIds(): string[] {
  return Object.keys(fieldMapperRegistry);
}

// Re-export mappings for direct access
export { mapWotcFields } from './wotcMapping';
export { mapIgnusArcanaFields } from './ignusArcanaMapping';
export { mapSrdHtmlFields } from './srdMapping';
